import { optimizedApi } from './api/optimized-api';
import { Column } from '../types/columns';

interface ColumnsApiResponse {
    columns: Column[];
}

interface UserColumnsApiResponse {
    success: boolean;
    total_columns: number;
    selected_columns: number;
    is_first_time: boolean;
    columns: Column[];
}

export class ColumnsService {
    /**
     * Get columns from API
     * Server-side caching is handled by Redis (30 minutes)
     */
    static async getColumns(includeOptions = true): Promise<{ success: boolean; columns: Column[]; fromCache?: boolean }> {
        try {
            // Fetch from API - server handles caching
            const response = await optimizedApi.get<ColumnsApiResponse>('/columns', {
                limit: 1000, // Get all columns, not just first 100
                include_options: includeOptions,
                active: true,
                page: 1
            });

            if (response.status === 'success' && response.data && Array.isArray(response.data.columns)) {
                return {
                    success: true,
                    columns: response.data.columns,
                    fromCache: false
                };
            } else {
                console.error('❌ Invalid columns response:', response);
                return {
                    success: false,
                    columns: []
                };
            }
        } catch (error) {
            console.error('❌ Error fetching columns:', error);
            return {
                success: false,
                columns: []
            };
        }
    }

    /**
     * Get user-selected columns from API
     * Server-side caching is handled by Redis (30 minutes)
     */
    static async getUserColumns(): Promise<{ success: boolean; columns: Column[]; fromCache?: boolean }> {
        try {
            // Fetch from API - server handles caching
            const response = await optimizedApi.get<UserColumnsApiResponse>('/user-leads-settings/columns') as any as UserColumnsApiResponse;

            // Get columns from response
            const columns = response.columns;

            if (response.success && Array.isArray(columns)) {
                return {
                    success: true,
                    columns: columns,
                    fromCache: false
                };
            } else {
                console.error('❌ Invalid user columns response:', response);
                return {
                    success: false,
                    columns: []
                };
            }
        } catch (error) {
            console.error('❌ Error fetching user columns:', error);
            return {
                success: false,
                columns: []
            };
        }
    }

    /**
     * Clear user column preferences (UI state only)
     * Note: Column data caching is handled by server-side Redis
     */
    static clearCache(): void {
        localStorage.removeItem('crm_user_column_preferences'); // Clear UI preferences only
    }

    /**
     * Refresh columns from API
     */
    static async refreshColumns(includeOptions = true): Promise<{ success: boolean; columns: Column[] }> {
        return this.getColumns(includeOptions);
    }
}

export default ColumnsService;